import { Component, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms'
import { OverlayPanel } from 'primeng/overlaypanel'
import { BrowserCompatibilityService } from '../../../../../../services/browser-compatibility.service'
import { Observable, Subscription } from 'rxjs'
import { distinctUntilChanged, map, skip } from 'rxjs/operators'
import { isEqual } from 'lodash'

@Component({
  selector: 'jml-lib-float-filter',
  templateUrl: './float-filter.component.html',
  styleUrls: ['./float-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FloatFilterComponent),
      multi: true
    }
  ]
})
export class FloatFilterComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() header
  @Input() decimals
  @ViewChild('op') op: OverlayPanel

  floatFilterOptions = [
    { label: 'Ist', value: 'IS' },
    { label: '≤', value: 'LT_EQ' },
    { label: '≥', value: 'GT_EQ' },
    { label: 'Zwischen', value: 'BETWEEN' }
  ]
  filterForm: UntypedFormGroup
  isActive = false
  subscriptions: Subscription[] = []
  showDialog = false
  filterRepr$: Observable<string>
  onTouched = (obj) => {}
  onChange = (obj) => {}
  isDisabled = false

  constructor(
    private fb: UntypedFormBuilder,
    private browserCompatibilityService: BrowserCompatibilityService
  ) {}
  writeValue(obj: any): void {
    if (obj) {
      this.filterForm.setValue({
        matchMode: obj.matchMode,
        floatFrom: String(obj.floatFrom).replace('\.', ','),
        floatTo: obj.floatTo
      })
    } else {
      this.filterForm.reset()
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled
  }

  ngOnInit(): void {
    // this.filterForm = this.rootFormGroup.control.get(this.formGroupName) as FormGroup;
    const floatPattern = '^-?\\d{1,3}((\\.)?\\d{3})*(,\\d{1,' + (this.decimals ? this.decimals : '') + '})?$'
    this.filterForm = this.fb.group({
      matchMode: new UntypedFormControl('IS'),
      floatFrom: new UntypedFormControl(undefined, [Validators.required, Validators.pattern(floatPattern)]),
      floatTo: new UntypedFormControl(undefined, [Validators.required, Validators.pattern(floatPattern)])
    })

    this.subscriptions.push(
      this.filterForm.valueChanges.subscribe(() => {
        const matchMode = this.filterForm.get('matchMode').value
        let isActive = this.filterForm.get('floatFrom').valid
        if (matchMode === 'BETWEEN') isActive = isActive && this.filterForm.get('floatTo').valid
        this.isActive = isActive
      })
    )

    this.filterRepr$ = this.filterForm.valueChanges.pipe(
      map(() => {
        // Build filter representation string
        if (!this.isActive) {
          return ''
        }

        const matchMode = this.filterForm.get('matchMode').value
        const floatFromValue = this.filterForm.get('floatFrom').value
        const floatToValue = this.filterForm.get('floatTo').value

        let filterRepr = ''
        if (matchMode !== 'BETWEEN') {
          if (matchMode === 'LT_EQ') {
            filterRepr = '≤ '
          } else if (matchMode === 'GT_EQ') {
            filterRepr = '≥ '
          } else {
            filterRepr = 'ist '
          }

          filterRepr += floatFromValue
        } else {
          filterRepr = `${floatFromValue} - ${floatToValue}`
        }
        return filterRepr
      })
    )

    this.subscriptions.push(
      this.filterForm.valueChanges
        .pipe(
          skip(3), // On initialization each control will emit once for the initial value. Since no actual change has happened dont let control emit
          distinctUntilChanged((prev, curr) => isEqual(prev, curr))
        )
        .subscribe((obj) => {
          if (!obj || (!obj.floatFrom && !obj.floatTo)) {
            this.onChange(null)
            this.onTouched(null)
            return
          }
          this.onChange(obj)
          this.onTouched(obj)
        })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe())
  }

  onIconClick(event) {
    if (this.browserCompatibilityService.isSmallScreen() || this.browserCompatibilityService.isCoarsePointer()) {
      this.showDialog = true
    } else {
      this.op.toggle(event)
    }
  }

  resetFilter() {
    this.showDialog = false
    this.op.hide()
    this.filterForm.reset()
  }

  private static formatInput(input: string): string {
    if (!input || input === '') return 'NaN'
    input = input.toString().replace(',', '.')
    return (+input).toString()
  }

  // Format current float to filter expression and emit through filter output
  public static formatFilterValue(matchMode, floatValueFrom, floatValueTo): string {
    floatValueFrom = this.formatInput(floatValueFrom)
    floatValueTo = this.formatInput(floatValueTo)
    let formattedValue = undefined

    if (floatValueFrom === 'NaN') return
    if (matchMode !== 'BETWEEN') {
      switch (matchMode) {
        case 'LT_EQ':
          formattedValue = `None, ${floatValueFrom}`
          break
        case 'GT_EQ':
          formattedValue = `${floatValueFrom}, None`
          break
        default:
          formattedValue = floatValueFrom
          break
      }
    } else if (floatValueTo !== 'NaN') {
      formattedValue = `${floatValueFrom}, ${floatValueTo}`
    }

    return formattedValue
  }
}
